// 열람 화면(S2) — **이 스텝에서 이 제품이 존재하는 이유가 성립한다**
//
// 앞의 다섯 스텝이 만든 것은 전부 여기로 오는 길이었다. 여기서 roster 가 실제로 자기 개시
// 시각을 본다.
//
// ── 위가 신호 존, 아래가 목록 ───────────────────────────────────────────────
//
// 위에는 지금 듣고 있는 하나가 크게 있고, 아래에는 이번 작전 전체가 있다. 순서를 뒤집으면
// 자기 것을 찾는 데 목록을 훑어야 하고, 그 훑기가 개시 직전에 일어난다.
//
// ── 시계는 이 화면이 한 번만 읽는다 ─────────────────────────────────────────
//
// 상단 고정줄과 신호 존과 카드가 전부 같은 `nowMs` 를 쓴다. 각자 읽으면 같은 화면 안의 두
// 숫자가 서로 다른 순간을 가리키고, 그 어긋남은 밀리초라 눈에 안 보인다. **서버가 보내는
// 틱이 아니라 자기 동기 시계 위에서 그린다** — 틱을 받아 그리면 네트워크 지터가 그대로
// 시각이 된다.
//
// ── 선택은 여기 살고 여기서만 산다 ──────────────────────────────────────────
//
// 고른 것은 이 브라우저의 메모리에만 있다. 서버로 올리지 않으므로 **남의 화면을 바꾸지
// 못한다**는 규칙이 지켜지는 것이 아니라 깨질 수 없다. 저장소에도 남기지 않는다 — 새로고침
// 뒤에 사람이 고른 적 없는 소리가 되살아나는 것이 이 화면에서 가장 나쁜 종류의 친절이다.

import { useEffect, useMemo, useState } from 'react'

import { shell as shellStrings, view as viewStrings } from '../../../i18n/index.ts'
import { attackBand, attackBandText } from '../../shell/band.ts'
import { readToken, VIEWER_TOKEN_KEY } from '../../access/client.ts'
import { TopBar } from '../../shell/topbar.tsx'
import { useShellClock } from '../../shell/use-shell-clock.ts'
import { publishAttackRallies } from '../../signal/live-attack.ts'
import { publishSignalTarget } from '../../signal/live-target.ts'
import {
  EMPTY_OPEN_BOOK,
  openViewOf,
  watchOpen,
  type OpenBook,
} from '../../signal/open-outcome.ts'
import { NO_SELECTION, pruneMissing, toggleHighlight, toggleSignal } from '../../signal/selection.ts'
import { Cards, type SortAxis } from './cards.tsx'
import { useReducedMotion } from './meteor.ts'
import { SignalZone } from './signal-zone.tsx'
import { IconTile } from '../../ui/icons.tsx'
import { useSignal } from './use-signal.ts'
import { useViewSequence } from './use-view-sequence.ts'
import { useLang } from '../../shell/use-lang.ts'

export interface ViewAttackScreenProps {
  /**
   * 열람 화면이 defense 탭이라 이 화면이 마운트된 채 감춰져 있다. 행군 막대 메테오(사용자 2026-10-14 채택)의 착탄 확산만 이 값을 쓴다.
   * 감춰진 동안 착탄한 줄은 확산 없이 곧바로 침강한다(`meteor.ts` 의 `gateBurst`).
   */
  concealed?: boolean
}

export function ViewAttackScreen(props: ViewAttackScreenProps = {}): React.ReactElement {
  const clock = useShellClock()
  // 저장소에 든 하나를 본다. 화면마다 따로 들면 연맹원이 탭을 옮길 때마다 언어가 돌아간다.
  const { lang, choose: setLang } = useLang()
  const t = viewStrings(lang)
  const shellT = shellStrings(lang)

  const [token] = useState<string | null>(() => readToken(VIEWER_TOKEN_KEY))
  // **대체 이름을 여기서 한 번 준다.** 아래로는 빈 이름이 흐르지 않으므로 카드도 신호
  // 존도 defense 탭으로 내보내는 값도 그것을 다시 다루지 않는다.
  const store = useViewSequence(token, t.rosterGone)

  // **초기값은 공용 상수 하나다.** 화면마다 「아무것도 안 고른 상태」를 따로 적으면 그중
  // 하나가 언젠가 「하나쯤 골라 두면 편하지 않을까」가 된다.
  const [selection, setSelection] = useState(NO_SELECTION)
  const [axis, setAxis] = useState<SortAxis>('open')

  // 기기의 움직임 줄이기. 행군 막대 메테오(사용자 2026-10-14 「매우 강함 채택.」, `meteor.ts`)가 이 값일 때 정적으로 선다.
  const reducedMotion = useReducedMotion()

  // 시퀀스가 바뀌면 사라진 줄을 선택에서 걷어낸다. CT 는 이 사람의 선택을 모른 채 작전을
  // 접거나 줄을 뺀다. 안 걷어내면 없는 rally 의 카운트다운이 그럴듯하게 흐른다.
  const presentIds = useMemo(() => store.rows.map((row) => row.rally.id), [store.rows])
  useEffect(() => {
    setSelection((current) => pruneMissing(current, presentIds))
  }, [presentIds])

  const target = useMemo(() => {
    if (selection.signalRallyId === null) return null
    const row = store.rows.find((line) => line.rally.id === selection.signalRallyId)
    if (row === undefined) return null
    // 이름과 시각을 **한 값으로 묶어** 넘긴다. 따로 넘기면 이름이 빈 채 시각만 있는 호출이
    // 타입 검사를 통과하고, 그것이 곧 이름 없는 카운트다운이다.
    return { rallyId: row.rally.id, rosterName: row.rosterName, openMs: row.openMs }
  }, [selection.signalRallyId, store.rows])

  // **소리는 고른 하나에서만 난다.** 눈으로 따라가는 줄이 여럿이어도 이 훅에 넘기는 것은
  // 언제나 하나이고, 그래서 「소리 둘」은 막히는 것이 아니라 만들어질 수 없다.
  //
  // 화면과 **같은** `nowMs` 를 넘기는 것이 중요하다. 소리가 자기 시계를 따로 읽으면 귀와
  // 눈이 서로 다른 순간을 가리키고, 그 어긋남은 밀리초라 아무에게도 안 보인다.
  const signal = useSignal({ target, nowMs: clock.utcMillis })

  /**
   * **개시 순간에 일어난 일을 rally 마다 한 번 적고 얼린다** (T6 개정 둘째, 2026-10-04).
   *
   * defense 탭 상단 줄이 개시 뒤 「꺼져 있었습니다」, 「제때 울렸습니다」, 「울리지 않았습니다」를
   * 가르는 근거다. 여기서 적는 이유는 소리가 실제로 울렸는지를 런타임이 **이 화면의 시계로** 정하기
   * 때문이다. 같은 사실을 다른 시계로 재면 두 시계가 어긋난 동안 개시 전후의 끄기가 반대로
   * 분류된다. 규칙과 그 까닭은 `open-outcome.ts` 에 있다.
   *
   * 기록이 바뀌지 않은 틱에는 같은 참조가 돌아와 상태 갱신이 렌더를 일으키지 않는다.
   *
   * 시각은 렌더마다 서버 추정으로 새로 읽은 값이고, **시계 동기가 살아 있지 않은 틱은 적지 않는다**
   * (`open-outcome.ts` 의 `OpenBook` 주석). 동기를 잃으면 화면 시계가 기기 시각으로 떨어져 개시 앞뒤를
   * 잘못 가른다.
   */
  const [openBook, setOpenBook] = useState<OpenBook>(EMPTY_OPEN_BOOK)
  useEffect(() => {
    setOpenBook((book) =>
      watchOpen(
        book,
        clock.utcMillis,
        target === null
          ? null
          : {
              rallyId: target.rallyId,
              openMs: target.openMs,
              muted: signal.muted,
              state: signal.state,
            },
        clock.skew.state === 'live',
      ),
    )
  }, [target, signal.muted, signal.state, clock.utcMillis, clock.skew.state])
  const openView = openViewOf(openBook, target)
  const outcome = openView.outcome

  /**
   * **고른 것이 무엇인지 화면 밖으로 알린다** (2026-08-16 개정 회차).
   *
   * 열람 화면이 보기 탭 둘로 갈리면서 사람이 defense 탭에 서 있는 동안에도 여기 걸린
   * 소리가 그대로 운다. 그때 그 탭이 **누구 것인지를 말하지 못하면** 「이름 없는
   * 카운트다운은 존재하지 않는다」가 소리 쪽에서 깨진다 — 대리 관측이 오디오 예약 여섯이
   * 살아 있는 것을 세어 놓고 같은 화면이 「소리 신호가 없습니다」라고 말하는 것을 함께
   * 잡았다.
   *
   * **이 화면의 다른 동작은 한 줄도 바뀌지 않는다.** 내보내는 값은 이미 여기 있던 `target`
   * 그대로이고, 선택 상태를 위로 올리지 않았다 — 올리면 이 화면의 소리 예약·정렬·강조가
   * 함께 딸려 올라가 되돌릴 단위가 두 스텝에 걸친다.
   *
   * 떠날 때 비우는 것이 짝이다. 안 비우면 이 화면을 나간 뒤에도 다른 화면이 **이미 걷힌
   * 소리의 주인**을 계속 적는다.
   */
  useEffect(() => {
    publishSignalTarget(
      target === null
        ? null
        : {
            rosterName: target.rosterName,
            openMs: target.openMs,
            muted: signal.muted,
            outcome,
          },
    )
    return () => {
      publishSignalTarget(null)
    }
  }, [target, signal.muted, outcome])

  /**
   * confirm 한 attack rally 수를 껍데기에 알린다. defense 탭에 착지한 사람에게 「attack 쪽에
   * 고를 rally 가 있다」를 말하는 근거다 (T2 개정 둘째, `src/web/signal/live-attack.ts`).
   */
  useEffect(() => {
    publishAttackRallies(store.rows.length)
    return () => {
      publishAttackRallies(0)
    }
  }, [store.rows.length])

  const dispatched = store.sequence?.state === 'dispatched'

  /**
   * 상단 고정줄 아래층의 attack.
   *
   * **계산은 `src/web/shell/band.ts` 하나가 진다.** 구성 화면이 같은 함수를 불러 같은 순간 같은 T− 와
   * 요약을 낸다. T− 는 아직 열지 않은 줄 가운데 가장 이른 개시까지이고(2026-10-13 사용자 결정), 요약에
   * 절대 시각 성분이 없다. confirm 전에는 이 화면만 「ops 없음」을 유지한다.
   */
  const band = useMemo(
    () =>
      attackBandText(
        attackBand(
          {
            state: store.sequence?.state ?? null,
            rallyCount: store.sequence?.rallies.length ?? 0,
            rows: store.rows,
          },
          clock.utcMillis,
        ),
        shellT,
        'view',
      ),
    [store.sequence, store.rows, clock.utcMillis, shellT],
  )

  return (
    <>
      <TopBar
        utcMillis={clock.utcMillis}
        skew={clock.skew}
        // 아래층의 attack 두 상태. **내보내기 전에는 남은 시간 자리가 고정된 빈 칸이다** —
        // 숫자를 감추는 것이 아니라 아직 존재하지 않는다. 요약에 절대 시각 성분을 넣지
        // 않는다: 그 자리는 delta 최소 요약이다.
        // 「다음」 요약의 조각(`summaryParts`)도 함께 넘긴다. 좁은 폭에서 간격 숫자가 잘리지 않게 고정줄이 조각마다 줄이는 순서를 준다.
        lower={{ opType: 'attack', ...band }}
        lang={lang}
        onLangChange={setLang}
      />

      {/* **재는 배선.** 마지막 스텝이 판정할 두 숫자를 화면이 들고 있게 한다 — 신호 누락은
          판정 계약이 0을 요구하는 값이고(평균이 아니라 한 번이라도 나면 불통), 재예약
          횟수는 실기기에서 이 실패가 실제로 일어나는지와 임계를 올릴지의 근거다.
          사람에게 보이는 자리가 아니라 검사가 읽는 자리이므로 속성으로 둔다.
          dispatch 에서 이 화면까지의 전파 시간은 여기 없다 — 계약에 dispatch 시각이
          없고 이 스텝은 계약을 열지 않기로 했으므로, 그것은 브라우저 검증이 양끝을 직접
          재는 편이 맞다. 작전 구성 스텝이 같은 값을 그렇게 쟀다. */}
      <main
        className="view-main col-narrow pt-group pb-block font-sans"
        data-testid="view-screen"
        data-missed-count={String(signal.observations.missedCount)}
        data-reschedule-count={String(signal.observations.rescheduleCount)}
      >
        {/* 2026-10-13 사용자가 c2 비교판을 보고 고름(attack 만, 수비 열람 제목은 화면에 남김): 「ops 열람」 제목 줄을 보이는 화면에서 걷는다. 화면 읽개에는 제목이 그대로
            남아 제목 구조가 깨지지 않는다. 신호 존이 고정줄 바로 아래 12px 로 올라온다. 화면 이름은 아래 탭 「ATTACK 보기」가 대신 말한다. */}
        <h1 className="sr-only text-lg font-semibold">{t.title}</h1>

        {token === null ? (
          // **입장하지 않은 사람이 주소만 알고 들어올 수 있다.** 연맹원끼리 링크를 주고받는
          // 것이 이 제품의 실제 쓰임이라 예외가 아니라 흔한 경로다. 그때 화면이 아무 말도
          // 하지 않으면 **미구현인지 고장인지 자기가 뭘 잘못했는지** 구별되지 않고, 누를
          // 것이 언어 전환뿐인 화면 앞에서 사람은 앱을 닫는다. 무엇이 필요한지와 어디로
          // 가면 되는지를 함께 말한다. (2026-08-14 대리 관측이 잡았다.)
          <section data-testid="view-need-entry" className="card mt-block">
            <div className="panel-head cat-accent">
              <IconTile name="key" tone="accent" />
              <h2 className="text-base font-semibold">{t.needEntryTitle}</h2>
            </div>
            <p className="mt-line text-sm text-ink-dim">{t.needEntryBody}</p>
            {/* 표시 방식에 inline 과 block 을 하이픈으로 이은 클래스를 쓰지 않는다. 간격 토큰 `--spacing-block` 과
                이름이 겹쳐 Tailwind 가 inline-size 유틸리티(26px 폭)로도 풀어, 글자가 한 자씩 세로로 섰다(2026-10-06
                배포 준비도 검토). 그 낱말은 주석에도 적지 않는다. Tailwind 는 주석의 글자도 클래스로 읽는다. */}
            <a
              data-testid="view-go-entry"
              href="/"
              // 44px 터치 하한(2026-10-12 대리 관측이 114x30 을 잡음). 구성 화면의 「관리 코드 넣기」와 같은 높이다.
              // 높이를 최소 높이가 아니라 위아래 여백 12px 로 세운다(글자 줄 20px + 24px). 한 줄 판정 검사는 높이에서 여백을 빼고 글자 줄
              // 수를 세므로, 여백 없이 최소 높이만 주면 한 줄인 버튼이 두 줄로 읽힌다.
              className="btn btn-primary mt-block py-3"
            >
              {t.needEntryCta}
            </a>
          </section>
        ) : !store.loaded ? (
          // 껍데기와 값을 가르는 자리. 값이 오기 전에는 목록도 신호 존도 그리지 않는다 —
          // 빈 목록을 먼저 그리면 「작전이 없다」와 「아직 못 읽었다」가 같은 화면이 된다.
          <p className="mt-block text-sm text-ink-mute">{t.loading}</p>
        ) : !dispatched ? (
          <section data-testid="view-waiting" className="card mt-block">
            <div className="panel-head cat-accent">
              <IconTile name="clock" tone="accent" />
              <h2 className="text-base font-semibold">{t.waitingTitle}</h2>
            </div>
            <p className="mt-line text-sm text-ink-dim">{t.waitingBody}</p>
          </section>
        ) : (
          <>
            {/* 감싸는 상자를 두지 않는다. 신호 존의 머리가 sticky 로 붙어 있으려면 부모가 목록까지 감싸는 `main` 이어야
                한다(`signal-zone.tsx`). */}
            <SignalZone
              lang={lang}
              target={target}
              nowMs={clock.utcMillis}
              signal={signal}
              {...openView}
            />

            <Cards
              lang={lang}
              rows={store.rows}
              nowMs={clock.utcMillis}
              axis={axis}
              onAxisChange={setAxis}
              signalRallyId={selection.signalRallyId}
              highlighted={selection.highlighted}
              onToggleSignal={(rallyId) => {
                setSelection((current) => toggleSignal(current, rallyId))
              }}
              onToggleHighlight={(rallyId) => {
                setSelection((current) => toggleHighlight(current, rallyId))
              }}
              reducedMotion={reducedMotion}
              concealed={props.concealed ?? false}
            />

            <p className="mt-block text-xs text-ink-mute">{t.selectionLocal}</p>
          </>
        )}
        {/* **아래 고정 영역 높이(`--dock-h`)만큼의 빈 칸을 이 `main` 안 맨 끝에 둔다** (T6 개정 셋째). 바깥 빈 칸이면
            화면이 낮은 가로 폰에서 페이지 끝 가까이 `main` 이 먼저 끝나, 그 안에 붙은 신호 존 머리가 함께 밀려 이름만 상단
            고정줄 밑에 숨었다(적대 리뷰, 세션이 640x300 에서 재현). 여백(padding)으로는 막지 못했다. sticky 의 한계선이
            부모의 내용 영역이라 빈 칸은 요소여야 한다(세션이 둘을 견줘 확인). */}
        <div aria-hidden="true" data-testid="view-dock-spacer" className="h-[var(--dock-h,0px)]" />
      </main>
    </>
  )
}
